import { footerNavigation } from '@/constants/navigation';
import { clearCookiePreferences } from '../../utils/cookieManager';

/**
 * Footer component for the Raidu website
 * Displays company information, navigation links, and social media icons
 * 
 * @returns {JSX.Element} The rendered Footer component
 */
export default function Footer() {
  try {
    // No state needed as we're using localStorage and page reload
    
    // Function to open cookie preferences
    const openCookiePreferences = () => {
      // This will trigger the CookieConsent component to show preferences
      // We need to clear the consent first to make the component visible
      clearCookiePreferences();
      // Set a flag in localStorage to indicate we want to show preferences
      localStorage.setItem('raidu_show_cookie_preferences', 'true');
      // Force a reload to show the cookie consent banner with preferences open
      window.location.reload();
    };
    
    // Get current year for copyright
    const currentYear = new Date().getFullYear();
    
    return (
      <footer className="relative overflow-hidden">
        {/* Decorative elements matching Hero section */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none z-0">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
        </div>
        
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none z-0" 
          style={{
            backgroundImage: 'radial-gradient(#4f46e5 0.5px, transparent 0.5px)',
            backgroundSize: '12px 12px'
          }}
        ></div>
        
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-8 sm:pt-24 lg:px-8 lg:pt-32 relative z-10">
          <div className="xl:grid xl:grid-cols-3 xl:gap-8">
            <div className="space-y-8">
              <img
                alt="Raidu"
                src="/logos/raidu_logo.png"
                className="h-16"
              />
              <p className="text-sm/6 text-balance text-gray-600">
                Enabling responsible AI adoption through governance, security, and compliance solutions.
              </p>
              <div className="flex gap-x-6">
                {footerNavigation.social.map((item) => {
                  // Create a simple icon component based on the iconName
                  const SocialIcon = () => {
                    if (item.iconName === 'linkedin') {
                      return (
                        <svg fill="currentColor" viewBox="0 0 24 24" className="size-6" aria-hidden="true">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      );
                    } else if (item.iconName === 'twitter') {
                      return (
                        <svg fill="currentColor" viewBox="0 0 24 24" className="size-6" aria-hidden="true">
                          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
                        </svg>
                      );
                    } else if (item.iconName === 'github') {
                      return (
                        <svg fill="currentColor" viewBox="0 0 24 24" className="size-6" aria-hidden="true">
                          <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                        </svg>
                      );
                    }
                    return null;
                  };
                  
                  return (
                    <a key={item.name} href={item.href} className="text-gray-600 hover:text-gray-800">
                      <span className="sr-only">{item.name}</span>
                      <SocialIcon />
                    </a>
                  );
                })}
              </div>
            </div>
            <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm/6 font-semibold text-gray-900">Why Raidu?</h3>
                  <ul role="list" className="mt-6 space-y-4">
                    {footerNavigation.whyRaidu.map((item) => (
                      <li key={item.name}>
                        <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-10 md:mt-0">
                  <h3 className="text-sm/6 font-semibold text-gray-900">Industries</h3>
                  <ul role="list" className="mt-6 space-y-4">
                    {footerNavigation.industries.map((item) => (
                      <li key={item.name}>
                        <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm/6 font-semibold text-gray-900">Company</h3>
                  <ul role="list" className="mt-6 space-y-4">
                    {footerNavigation.company.map((item) => (
                      <li key={item.name}>
                        <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-10 md:mt-0">
                  <h3 className="text-sm/6 font-semibold text-gray-900">Legal</h3>
                  <ul role="list" className="mt-6 space-y-4">
                    {footerNavigation.legal.map((item) => (
                      <li key={item.name}>
                        <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 lg:mt-24">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <p className="text-sm/6 text-gray-600">&copy; {currentYear} Raidu, Inc. All rights reserved. Secure, compliant, and responsible AI for the enterprise.</p>
              <button
                onClick={openCookiePreferences}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
                Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </footer>
    );
  } catch (error) {
    // Log any errors that occur during rendering
    console.error('Error rendering Footer component:', error);
    
    // Fallback UI in case of error
    return (
      <footer className="bg-white p-6 text-center">
        <p className="text-gray-600">An error occurred while loading the footer. Please try refreshing the page.</p>
      </footer>
    );
  }
}
